<template>
	<InventarisationPageTemplate v-slot="{ selectedIndex, customers }">
		<InventarisationAppendSectionMenu
			:sections
			class="mb-10"
			@append="appendHandler"
		/>

		<FormKit
			v-if="customers[selectedIndex] && customer"
			:key="customer.customerId"
			v-model="customer.sections"
			:config
			type="form"
			:actions="false"
		>
			<div class="max-w-2xl pb-32">
				<InventarisationRepeatableSection
					v-for="section in sections"
					:key="section.type"
					:section="section"
					@remove="(id) => removeHandler(section.type as AssetSectionKeys, id)"
					@insert="() => appendHandler(section.type)"
				/>
				<DashboardCardCollapsible v-if="SHOW_DEBUG" heading="DATA DEBUG">
					<pre><code>{{ JSON.stringify(customer.sections, null, 2) }}</code></pre>
				</DashboardCardCollapsible>
			</div>
		</FormKit>
	</InventarisationPageTemplate>
</template>

<script lang="ts" setup>
import { rootClasses } from "../../../formkit.theme";
import InventarisationAssetsSavingsAccountForm from "@/components/inventarisation/assets/InventarisationAssetsSavingsAccountForm.vue";
import InventarisationAssetsOtherForm from "@/components/inventarisation/assets/InventarisationAssetsOtherForm.vue";
import InventarisationAssetsSavingsPlanForm from "@/components/inventarisation/assets/InventarisationAssetsSavingsPlanForm.vue";
import InventarisationAssetsInvestmentAccountForm from "@/components/inventarisation/assets/InventarisationAssetsInvestmentAccountForm.vue";
import type { AssetSectionKeys, CustomerAssets } from "@loanlink-nl/model-app";
import { computed, nextTick, watch } from "vue";
import { useInventarisationStore } from "@/store/useInventarisationStore";
import { storeToRefs } from "pinia";

const inventarisationStore = useInventarisationStore();
const { selectedCustomerId: storeSelectedCustomerId } =
	storeToRefs(inventarisationStore);

const SHOW_DEBUG = import.meta.env.DEV;

const {
	customerHasEntry,
	addCustomerEntry,
	getCustomerEntry,
	appendSection,
	removeSection,
	getSectionIndex,
	getSection,
	removeInvalidValues,
} = inventarisationStore.assets;

// Ensure customer entry exists when selection changes
watch(
	storeSelectedCustomerId,
	(newSelectedCustomerId) => {
		if (
			newSelectedCustomerId !== undefined &&
			!customerHasEntry(newSelectedCustomerId)
		) {
			addCustomerEntry(newSelectedCustomerId);
		}
		if (newSelectedCustomerId !== undefined) {
			void nextTick(() => {
				removeInvalidValues(newSelectedCustomerId);
			});
		}
	},
	{ immediate: true },
);

// Reactive customer entry - returns the actual reactive object from the store
// Mutations to customer.sections will propagate back to the source
const customer = computed<CustomerAssets | undefined>(() => {
	if (storeSelectedCustomerId.value === undefined) {
		return undefined;
	}
	return getCustomerEntry(storeSelectedCustomerId.value).value;
});

const removeHandler = (section: string, id: string) => {
	if (storeSelectedCustomerId.value) {
		removeSection(
			storeSelectedCustomerId.value,
			section as AssetSectionKeys,
			id,
		);
	}
};

const appendHandler = (section: string) => {
	if (storeSelectedCustomerId.value) {
		appendSection(storeSelectedCustomerId.value, section as AssetSectionKeys);
	}
};

const getDefaultHeadingFunction =
	(sectionKey: AssetSectionKeys, label: string, detailsKey?: string) =>
	(id: string) => {
		if (storeSelectedCustomerId.value === undefined) {
			return label;
		}

		const section = getSection(storeSelectedCustomerId.value, sectionKey, id);
		if (
			detailsKey &&
			section?.values &&
			detailsKey in section.values &&
			section.values[detailsKey as keyof (typeof section)["values"]]
		) {
			const sectionName = Object.keys(section.values).includes(detailsKey)
				? // @ts-expect-error - TypeScript doesn't know that the detailsKey is a key of the section.values object
					section.values[detailsKey]
				: "";
			return `${label} (${sectionName})`;
		}

		const sectionIndex = getSectionIndex(
			storeSelectedCustomerId.value,
			sectionKey,
			id,
		);
		return `${label} #${sectionIndex + 1}`;
	};

const sections = [
	{
		type: "savingsAccount",
		appendLabel: "Spaarrekening",
		component: InventarisationAssetsSavingsAccountForm,
		getHeading: getDefaultHeadingFunction(
			"savingsAccount",
			"Spaarrekening",
			"bank",
		),
	},
	{
		type: "investmentAccount",
		appendLabel: "Beleggingsrekening",
		component: InventarisationAssetsInvestmentAccountForm,
		getHeading: getDefaultHeadingFunction(
			"investmentAccount",
			"Beleggingsrekening",
			"company",
		),
	},
	{
		type: "savingsPlan",
		appendLabel: "Spaarloon of levenslooprekening",
		component: InventarisationAssetsSavingsPlanForm,
		getHeading: getDefaultHeadingFunction(
			"savingsPlan",
			"Spaarloon of levenslooprekening",
			"bank",
		),
	},
	{
		type: "other",
		appendLabel: "Overige bezittingen",
		component: InventarisationAssetsOtherForm,
		getHeading: getDefaultHeadingFunction(
			"other",
			"Overige bezittingen",
			"description",
		),
	},
];

const config = {
	rootClasses,
};
</script>
